import { defineStore } from "pinia";
import { AxiosError, type AxiosRequestConfig } from "axios";
import api from "@/_helpers/api";
import router from "@/_helpers/router";
import { START_LOCATION } from "vue-router";
import { axiosInstance } from "@/plugins/https";
import { useDialogsStore } from "@/_store/dialogs.module";
import { AccountErrors, MfaStatus } from "@/constants/account";
import { parseJwt } from "@coronet/cui";
import { navigateWithReload } from "@/_helpers/navigation";
import { resetPersistedStores } from "@/_helpers/workspace";
import cloneDeep from "lodash/cloneDeep";
import type { Service } from "@/constants/cloud-apps";
import { WorkspaceLocale } from "@/constants/workplaces";
import { useSnackbarStore } from "@/_store/snackbar.module";
import { i18n } from "@/plugins/i18n";
import { useMyAccountStore } from "@/_store/my-account.module";
import { RouteName } from "@/constants/routes";
import camelCase from "lodash/camelCase";
import { useMfaStore } from "@/_store/mfa.module";
import { useTutorialStore } from "@/_store/tutorial.module.ts";
import { useGtm } from "@gtm-support/vue-gtm";
import { GtmAction } from "@/constants/gtm.ts";
import { useWorkspaceStore } from "@/_store/workspace.module.ts";
import { StoreModule } from "@/constants/store.ts";
import isEmpty from "lodash/isEmpty";
import { getRecaptchaToken } from "@/_helpers/recaptcha";

export interface LoginData {
  token: string;
  refreshToken: string;
  mfaConfig: MfaStatus;
}

type AccountState = {
  error: string;
  requestInProgress: boolean;
  requestCompleted: boolean;
  email: string;
  token: string;
  refreshToken: string;
  service: string;
  inviteAccepted: boolean;
  socialLogin: boolean;
  hasMultipleWorkspaces: boolean;
  hasMspAccess: boolean;
};

const defaultAccountState: AccountState = {
  error: "",
  requestInProgress: false,
  requestCompleted: false,
  email: "",
  token: "",
  refreshToken: "",
  service: "",
  inviteAccepted: false,
  socialLogin: false,
  hasMultipleWorkspaces: false,
  hasMspAccess: false,
};

export const useAccountStore = defineStore(StoreModule.ACCOUNT, {
  state: (): AccountState => cloneDeep(defaultAccountState),
  persist: true,
  getters: {
    logged(state) {
      return Boolean(state.email && state.token && state.refreshToken);
    },
    displayLanguage() {
      // Until the first navigation lands, `currentRoute` is START_LOCATION, whose meta is empty —
      // the route being opened is the URL's (public auth pages follow the browser language).
      const route =
        router.currentRoute.value === START_LOCATION
          ? router.resolve(window.location.pathname + window.location.search + window.location.hash)
          : router.currentRoute.value;
      const routePreferredLocale = route.meta.preferredLocale;
      if (routePreferredLocale) {
        return routePreferredLocale;
      }
      const myAccountStore = useMyAccountStore();
      const workspaceStore = useWorkspaceStore();
      const preferredLanguage = myAccountStore.myAccount.profileData.preferredLanguageCode;
      return preferredLanguage || workspaceStore.workspace.languageCode || WorkspaceLocale.EN_US;
    },
  },
  actions: {
    async login(credentials: { userName: string; password: string }) {
      const workspaceStore = useWorkspaceStore();
      const brandingAlias = workspaceStore.brandingAlias;
      const params = brandingAlias && { brandingAlias };
      const request: AxiosRequestConfig = {
        ...api.login,
        data: credentials,
        params,
      };

      this.requestInProgress = true;
      try {
        const token = await getRecaptchaToken();
        request.headers = { "g-recaptcha-response": token };

        const { data } = await axiosInstance.request(request);
        await this.processLoginData(data);
        this.requestInProgress = false;
      } catch (e: unknown) {
        if (e instanceof AxiosError && e.response) {
          if (e.response.status === 403) {
            this.error = camelCase((e.response.data as { message: string }).message);
          }
        } else {
          this.error = AccountErrors.GENERIC;
        }
        this.requestInProgress = false;
        console.error(e);
      }
    },
    async loginViaSSO(token: string) {
      const request = {
        ...api.loginViaSSO(token),
      };

      this.setError("");
      this.setRequestInProgress(true);

      try {
        const res = await axiosInstance.request(request);
        await this.processLoginData(res.data);
      } catch (e) {
        const error = e as AxiosError<{ message?: string }>;
        if (!error.response || !error.response.data.message) {
          this.setError(i18n.global.t(`sso.errors.${AccountErrors.GENERIC}`));
        } else {
          this.setError(error.response.data.message);
        }
      } finally {
        this.setRequestInProgress(false);
      }
    },
    async socialLogin(service: Service) {
      const workspaceStore = useWorkspaceStore();
      const brandingAlias = workspaceStore.brandingAlias;
      const request = {
        ...api.socialLogin(service, brandingAlias),
      };
      this.service = service;
      try {
        const { data } = await axiosInstance.request(request);
        window.location.href = data.redirectUrl;
      } catch (e) {
        console.error(e);
      }
    },
    async socialSignUp(
      service: Service,
      utmParams: object,
      languageCode: WorkspaceLocale
    ): Promise<void> {
      const workspaceStore = useWorkspaceStore();
      const brandingAlias = workspaceStore.brandingAlias;
      const request = {
        ...api.socialSignUp(),
        params: {
          service,
          brandingAlias,
          languageCode,
          ...utmParams,
        },
      };
      try {
        const { data } = await axiosInstance.request(request);
        window.location.href = data.redirectUrl;
      } catch (e) {
        console.error(e);
      }
    },
    async verifyPublicEmail(
      email: string
    ): Promise<{ valid: true } | { valid: false; message: string }> {
      try {
        const token = await getRecaptchaToken();
        const request = {
          ...api.verifyPublicEmail(email),
          headers: { "g-recaptcha-response": token },
        };

        await axiosInstance.request(request);
        return { valid: true };
      } catch (e) {
        console.error(e);
        const error = e as AxiosError<{ errors: [string] }>;
        const [message] = error.response?.data.errors ?? [i18n.global.t("errors.generic")];
        return { valid: false, message };
      }
    },
    async requestSignUpVerificationCode({
      email,
      languageCode,
      password,
    }: {
      email: string;
      password: string;
      languageCode: WorkspaceLocale;
    }): Promise<{ success: boolean; message?: string }> {
      try {
        const token = await getRecaptchaToken();

        const request: AxiosRequestConfig = {
          ...api.requestSignUpVerificationCode(),
          method: "POST",
          data: {
            email,
            password,
            languageCode,
          },
          headers: { "g-recaptcha-response": token },
        };

        await axiosInstance.request(request);
        return { success: true };
      } catch (e) {
        console.error(e);
        const error = e as AxiosError<{ errors: [string] }>;
        const [message] = error.response?.data.errors ?? [i18n.global.t("errors.generic")];
        return { success: false, message };
      }
    },
    async completeSignUp(
      payload: {
        email: string;
        password: string;
        languageCode: WorkspaceLocale;
        verificationCode?: string;
      },
      utmParams: { [key: string]: string } = {}
    ): Promise<{ success: true } | { success: false; message: string }> {
      try {
        const token = await getRecaptchaToken();
        const request = {
          ...api.completeSignUp(),
          method: "POST",
          data: {
            ...payload,
            utmParams,
          },
          headers: { "g-recaptcha-response": token },
        };
        const { data } = await axiosInstance.request<{ token: string; refreshToken: string }>(
          request
        );
        await this.processLoginData({ ...data, mfaConfig: MfaStatus.NONE });
        useTutorialStore().$patch({ isNewUser: true });
        const gtm = useGtm();
        if (gtm?.enabled()) {
          window.dataLayer?.push({ event: `${GtmAction.SIGNUP_CLICKED} - with email` });
        }
        return { success: true };
      } catch (err) {
        console.error(err);
        const error = err as AxiosError<{ errors: [string] }>;
        const [message] = error.response?.data.errors ?? [i18n.global.t("errors.generic")];
        return {
          success: false,
          message,
        };
      }
    },
    async processLoginData(payload: LoginData) {
      const workspaceStore = useWorkspaceStore();
      const hasNavigationFromEmail = false;
      const { refreshToken, token, mfaConfig } = payload;
      const { sub } = parseJwt(token) ?? {};
      const email = sub ?? this.email;
      this.$patch({ email });

      if (mfaConfig === MfaStatus.TOTP) {
        const mfaStore = useMfaStore();
        mfaStore.$patch({
          mfaFlowToken: token,
        });
        await router.push({ name: RouteName.MFA_VERIFICATION });
        return;
      }

      try {
        const workspacesRequest = {
          ...api.workplaces({
            name: "",
            type: "",
            page: 0,
            pageSize: 25,
          }),
          headers: {
            Authorization: `Bearer ${token}`, //the token is a variable which holds the token
          },
          method: "get",
        };

        const { data } = await axiosInstance.request(workspacesRequest);
        const multipleWorkspaces = data.total > 1;
        this.hasMultipleWorkspaces = multipleWorkspaces;
        if (multipleWorkspaces || hasNavigationFromEmail) {
          this.$patch({
            token,
            refreshToken,
          });
          await this.getProfileInformation(multipleWorkspaces);
          this.requestInProgress = false;
        } else {
          const workspace = data.items[0];
          this.$patch({
            token,
            refreshToken,
          });
          workspaceStore.$patch((state) => {
            state.workspace.workspaceId = workspace.workspaceId;
          });
          await this.getProfileInformation(multipleWorkspaces);
          await workspaceStore.updateWorkspaceAndPermissions(workspace.workspaceId, false);
        }
      } catch (e) {
        console.error(e);
      }
    },
    async getProfileInformation(multipleWorkspaces: boolean) {
      const myAccountStore = useMyAccountStore();
      try {
        await myAccountStore.getProfileData();
        if (multipleWorkspaces) {
          await router.push({ name: RouteName.WORKSPACES });
        } else {
          await router.push({
            name: RouteName.DASHBOARD_PAGE,
          });
        }
      } catch (e) {
        console.error(e);
      }
    },
    async getRefreshToken() {
      return axiosInstance.request(api.refreshToken());
    },
    async logout() {
      try {
        await axiosInstance.request(api.logout());
      } catch (e) {
        console.error(e);
      } finally {
        this.$reset();
        resetPersistedStores();
        useDialogsStore().closeDialog();
        localStorage.setItem("logout-event", `logout${Math.random()}`);
        navigateWithReload(RouteName.LOGIN);
      }
    },
    async forgotPassword(payload: { email: string; showSnackbar?: boolean }) {
      const { email, showSnackbar } = payload;
      const workspaceStore = useWorkspaceStore();
      const brandingAlias = workspaceStore.brandingAlias;
      const request = {
        ...api.forgotPassword(email, brandingAlias),
      };
      try {
        await axiosInstance.request(request);
        if (showSnackbar) {
          useSnackbarStore().addGenericSuccess(
            i18n.global.t("snackbar.messages.resetPassword.linkSent", { email })
          );
        }
      } catch (e) {
        console.error(e);
        if (!(e as AxiosError).response) {
          this.error = AccountErrors.GENERIC;
        } else if ((e as AxiosError).response?.status === 404) {
          this.error = AccountErrors.EMAIL_NOT_FOUND;
        }
      }
    },
    async resetPassword(data: { token: string; newPassword: string }) {
      this.setRequestCompleted(false);
      this.setRequestInProgress(true);

      try {
        await axiosInstance.request({ ...api.resetPassword, data });
        this.setRequestCompleted(true);
      } catch (e) {
        console.error(e);

        const error = e as AxiosError<{ errors?: string[] }>;
        if (!error.response) {
          this.error = AccountErrors.GENERIC;
          return;
        }

        const status = error.response?.status;
        const [errorKey] = error.response?.data?.errors ?? [];

        if (status === 400) {
          const resetPasswordErrorKeys: string[] = [
            AccountErrors.RESET_LINK_INVALID,
            AccountErrors.WEAK_PASSWORD,
            AccountErrors.PENDING_INVITATION,
            AccountErrors.PASSWORD_SHOULD_BE_DIFFERENT,
          ];
          this.error =
            errorKey && resetPasswordErrorKeys.includes(errorKey)
              ? errorKey
              : AccountErrors.GENERIC;
        }
      } finally {
        this.setRequestInProgress(false);
      }
    },
    async checkMspAccess() {
      const request = {
        ...api.getRelevantHierarchy(),
      };

      try {
        const { data } = await axiosInstance.request(request);
        this.hasMspAccess = !isEmpty(data);
      } catch (error) {
        // 403 is the expected "not an MSP" answer — this endpoint is the only way to ask,
        // so it is not logged (RUM reports console errors) and `rumBeforeSend` drops the
        // network event. See PLAT-4382. Any other failure is real and still reported.
        if ((error as AxiosError).response?.status !== 403) {
          console.error(error);
        }
        this.hasMspAccess = false;
      }
    },
    async checkWorkspaceQuantityForUser() {
      try {
        const workspacesRequest = {
          ...api.workplaces({
            name: "",
            type: "",
            page: 0,
            pageSize: 25,
          }),
          method: "GET",
        };

        const { data } = await axiosInstance.request(workspacesRequest);
        this.hasMultipleWorkspaces = data.total > 1;
      } catch (error) {
        console.error(error);
      }
    },
    setError(value: string) {
      this.error = value;
    },
    setRequestInProgress(value: boolean) {
      this.requestInProgress = value;
    },
    setRequestCompleted(value: boolean) {
      this.requestCompleted = value;
    },
    setToken(payload: string) {
      this.token = payload;
    },
  },
});
